import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Users, Trash2, Plus, ShieldCheck, ChefHat, HandPlatter, Briefcase, KeyRound, Power, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ROLES = [
  { key: "manager", label: "Manager", desc: "Email + mot de passe. Opérations, encaissements, statistiques", icon: Briefcase, op: false },
  { key: "server", label: "Serveur / Caissier", desc: "Nom + PIN. Commandes, tables, ajout manuel, encaissements", icon: HandPlatter, op: true },
  { key: "kitchen", label: "Cuisine", desc: "Nom + PIN. KDS uniquement (aucun accès paiements/paramètres)", icon: ChefHat, op: true },
];
const ROLE_META = Object.fromEntries(ROLES.map((r) => [r.key, r]));
const isOp = (role) => role === "server" || role === "kitchen";
const DEFAULT_STATION = { server: "pos", kitchen: "kds" }; // poste natif du rôle
const STATION_LABELS = { pos: "Caisse", kds: "Cuisine" };

export default function StaffPanel({ slug }) {
  const { user } = useAuth();
  const isOwner = user?.role === "admin" || user?.role === "org_admin";
  const canEdit = (u) => isOwner || u.role !== "manager";
  const roleOptions = isOwner ? ROLES : ROLES.filter((r) => r.key !== "manager");
  const [renaming, setRenaming] = useState(null); // { id, name }
  const rename = async () => {
    if (!renaming?.name?.trim()) return;
    try { await api.updateStaff(slug, renaming.id, { name: renaming.name.trim() }); toast.success("Nom mis à jour"); setRenaming(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "kitchen", pin: "", confirmPin: "" });
  const [saving, setSaving] = useState(false);
  const [pinTarget, setPinTarget] = useState(null); // staff row for PIN reset dialog
  const [tempPin, setTempPin] = useState(null); // { pin, name } affiché une seule fois
  const [pinSaving, setPinSaving] = useState(false);
  const [pinLen, setPinLen] = useState(4);
  const [pinLenSaving, setPinLenSaving] = useState(false);
  const [pinLenConfirm, setPinLenConfirm] = useState(null); // target length awaiting confirmation

  const load = () => api.listStaff(slug).then(setStaff).catch(() => {});
  const loadPinLen = () => api.getRestaurant(slug).then((r) => setPinLen(r?.pin_length || 4)).catch(() => {});
  useEffect(() => { if (slug) { load(); loadPinLen(); } /* eslint-disable-next-line */ }, [slug]);

  const applyPinLength = async (target) => {
    setPinLenSaving(true);
    try {
      const res = await api.updatePinSettings(slug, target);
      setPinLen(target); setPinLenConfirm(null);
      toast.success(`Longueur de PIN réglée sur ${target} chiffres${res.reset_required_count ? ` · ${res.reset_required_count} PIN à réinitialiser` : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPinLenSaving(false); }
  };

  const savePin = async () => {
    setPinSaving(true);
    try {
      const r = await api.resetStaffPin(slug, pinTarget.id);
      setTempPin({ pin: r.temp_pin, name: r.name || pinTarget.name || pinTarget.email });
      setPinTarget(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPinSaving(false); }
  };

  const clearPin = async (row) => {
    try { await api.clearStaffPin(slug, row.id); toast.success("PIN réinitialisé"); load(); }
    catch (e) { toast.error("Erreur"); }
  };

  const changeRole = async (row, role) => {
    try { await api.updateStaff(slug, row.id, { role }); toast.success("Rôle mis à jour"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleActive = async (row) => {
    const willActivate = row.active === false;
    try { await api.updateStaff(slug, row.id, { active: willActivate }); toast.success(willActivate ? "Employé réactivé" : "Employé désactivé"); load(); }
    catch (e) { toast.error("Erreur"); }
  };

  const toggleStation = async (row, key) => {
    const cur = Array.isArray(row.extra_stations) ? row.extra_stations : [];
    const next = cur.includes(key) ? cur.filter((s) => s !== key) : [...cur, key];
    try { await api.updateStaff(slug, row.id, { extra_stations: next }); toast.success("Accès poste mis à jour"); load(); }
    catch (e) { toast.error("Erreur"); }
  };

  const create = async () => {
    const op = isOp(form.role);
    if (!form.name.trim()) { toast.error("Nom de l'employé requis"); return; }
    if (!op && (!form.email || !form.password)) {
      toast.error("Email et mot de passe requis pour un Manager"); return;
    }
    setSaving(true);
    try {
      const payload = op
        ? { name: form.name.trim(), role: form.role }
        : { name: form.name.trim(), role: form.role, email: form.email, password: form.password };
      const created = await api.createStaff(slug, payload);
      toast.success(`Compte ${ROLE_META[form.role].label} créé`);
      if (created?.temp_pin) setTempPin({ pin: created.temp_pin, name: created.name });
      setForm({ name: "", email: "", password: "", role: "kitchen", pin: "", confirmPin: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id, email) => {
    try { await api.deleteStaff(slug, id); toast.success("Compte supprimé"); setStaff((s) => s.filter((x) => x.id !== id)); }
    catch (e) { toast.error("Erreur"); }
  };

  return (
    <div className="rounded-2xl bg-[#111F1A] border border-[#1B3028] p-5 space-y-4" data-testid="staff-panel">
      <h3 className="font-serif-display text-lg flex items-center gap-2"><Users className="w-4 h-4 text-[#C69C4A]" /> Employés & rôles</h3>
      <p className="text-xs text-stone-500 -mt-2">Les permissions sont appliquées côté serveur (la Cuisine ne peut jamais encaisser).</p>

      {isOwner && <div data-testid="pin-security" className="rounded-xl bg-[#0A1712] border border-[#1B3028] p-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-stone-200 flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5 text-[#C69C4A]" /> Longueur du PIN</p>
          <p className="text-[11px] text-stone-500">Même longueur pour tous les employés. Changer la longueur invalide les PIN existants (à réinitialiser).</p>
        </div>
        <div className="flex gap-1.5 shrink-0">
          {[4, 6].map((n) => (
            <button key={n} data-testid={`pin-length-${n}`} disabled={pinLenSaving}
              onClick={() => { if (n !== pinLen) setPinLenConfirm(n); }}
              className={`w-16 h-9 rounded-full text-sm font-bold border transition-colors ${pinLen === n ? "bg-[#C69C4A] text-[#0C1713] border-[#C69C4A]" : "text-stone-300 border-[#1B3028] hover:border-[#C69C4A]/50"}`}>
              {n} chif.
            </button>
          ))}
        </div>
      </div>}
      {!isOwner && <p data-testid="staff-manager-scope" className="text-[11px] text-stone-500 rounded-xl bg-[#0A1712] border border-[#1B3028] p-2.5">En tant que Manager, vous gérez les serveurs, caissiers et cuisiniers. Les comptes Manager et la longueur du PIN restent gérés par le propriétaire.</p>}

      <div className="space-y-2">
        {staff.length === 0 && <p className="text-sm text-stone-600">Aucun employé pour le moment.</p>}
        {staff.map((u) => {
          const meta = ROLE_META[u.role] || { label: u.role, icon: ShieldCheck };
          const Icon = meta.icon;
          return (
            <div key={u.id} data-testid={`staff-row-${u.id}`} className={`flex items-center justify-between rounded-xl bg-[#0A1712] border border-[#1B3028] px-3 py-2.5 ${u.active === false ? "opacity-50" : ""}`}>
              <div className="min-w-0">
                {renaming?.id === u.id ? (
                  <div className="flex items-center gap-1.5">
                    <Input data-testid={`staff-rename-input-${u.id}`} autoFocus value={renaming.name} onChange={(e) => setRenaming({ ...renaming, name: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") rename(); if (e.key === "Escape") setRenaming(null); }} className="h-8 bg-[#0C1713] border-[#1B3028] text-stone-100 rounded-lg text-sm w-44" />
                    <button data-testid={`staff-rename-save-${u.id}`} onClick={rename} className="text-[11px] font-semibold text-[#C69C4A]">OK</button>
                  </div>
                ) : (
                  <p className="text-sm text-stone-100 truncate">{u.name || u.email}
                    {u.employee_code && <span data-testid={`staff-code-${u.id}`} className="ml-1.5 font-mono text-[11px] text-[#C69C4A]">{u.employee_code}</span>}
                    {canEdit(u) && <button data-testid={`staff-rename-${u.id}`} onClick={() => setRenaming({ id: u.id, name: u.name || "" })} title="Renommer" className="ml-1.5 text-stone-500 hover:text-[#C69C4A] align-middle"><Pencil className="w-3 h-3 inline" /></button>}
                    {u.active === false && <span className="ml-2 text-[10px] uppercase text-red-400">Désactivé</span>}
                  </p>
                )}
                <p className="text-xs text-stone-500 truncate">{u.login_type === "pin" || isOp(u.role) ? "Connexion par PIN" : u.email}</p>
              </div>
              {!canEdit(u) ? (
                <span data-testid={`staff-locked-${u.id}`} className="text-[11px] text-stone-500 flex items-center gap-1 shrink-0"><ShieldCheck className="w-3.5 h-3.5" /> Manager · géré par le propriétaire</span>
              ) : (
              <div className="flex items-center gap-2 shrink-0">
                <select data-testid={`staff-role-${u.id}`} value={u.role} onChange={(e) => changeRole(u, e.target.value)} className="bg-[#0C1713] border border-[#1B3028] rounded-lg px-2 py-1 text-[11px] text-[#C69C4A] font-semibold">
                  {roleOptions.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select>
                {isOp(u.role) && ["pos", "kds"].filter((s) => s !== DEFAULT_STATION[u.role]).map((s) => {
                  const on = (u.extra_stations || []).includes(s);
                  return (
                    <button key={s} data-testid={`staff-station-${s}-${u.id}`} onClick={() => toggleStation(u, s)} title={`Autoriser l'accès ${STATION_LABELS[s]}`}
                      className={`text-[10px] font-semibold rounded-full border px-2 py-1 transition-colors ${on ? "text-[#C69C4A] border-[#C69C4A]/50 bg-[#C69C4A]/10" : "text-stone-500 border-[#1B3028] hover:border-[#C69C4A]/40"}`}>
                      +{STATION_LABELS[s]}
                    </button>
                  );
                })}
                <button data-testid={`staff-pin-${u.id}`} onClick={() => setPinTarget(u)}
                  className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full border px-2.5 py-1 transition-colors ${u.pin_reset_required ? "text-amber-400 border-amber-500/50 hover:border-amber-500/80" : u.pin_temporary ? "text-sky-300 border-sky-500/40 hover:border-sky-500/70" : u.has_pin ? "text-emerald-400 border-emerald-500/40 hover:border-emerald-500/70" : "text-stone-400 border-[#1B3028] hover:border-[#C69C4A]/50"}`}>
                  <KeyRound className="w-3 h-3" /> {u.pin_reset_required ? "Réinit. requise" : u.pin_temporary ? "PIN provisoire" : u.has_pin ? "PIN personnel · réinitialiser" : "Générer un PIN"}
                </button>
                {u.has_pin && !u.pin_reset_required && <button data-testid={`staff-pin-clear-${u.id}`} onClick={() => clearPin(u)} className="text-[11px] text-stone-500 hover:text-red-400">Retirer</button>}
                <button data-testid={`staff-toggle-${u.id}`} onClick={() => toggleActive(u)} title={u.active === false ? "Réactiver" : "Désactiver"} className="text-stone-500 hover:text-amber-400 p-1"><Power className="w-4 h-4" /></button>
                <button data-testid={`staff-delete-${u.id}`} onClick={() => remove(u.id, u.email)} className="text-stone-500 hover:text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
              </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="border-t border-[#1B3028] pt-4 space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-stone-400">Nouvel employé</p>
        <div className="grid sm:grid-cols-2 gap-2">
          <Input data-testid="staff-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom de l'employé" className="bg-[#0A1712] border-[#1B3028] text-stone-100 rounded-xl" />
          <select data-testid="staff-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="bg-[#0A1712] border border-[#1B3028] rounded-xl px-3 text-sm text-stone-100 h-10">
            {roleOptions.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
          {isOp(form.role) ? (
            <p data-testid="staff-temp-pin-note" className="sm:col-span-1 text-xs text-stone-400 self-center">Un <b className="text-[#C69C4A]">PIN provisoire</b> sera généré et affiché une seule fois. L'employé choisira son PIN personnel à sa première connexion.</p>
          ) : (
            <>
              <Input data-testid="staff-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="bg-[#0A1712] border-[#1B3028] text-stone-100 rounded-xl" />
              <Input data-testid="staff-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Mot de passe" className="bg-[#0A1712] border-[#1B3028] text-stone-100 rounded-xl" />
            </>
          )}
        </div>
        <p className="text-[11px] text-stone-500">{ROLE_META[form.role]?.desc}</p>
        <Button data-testid="staff-create" onClick={create} disabled={saving} className="rounded-full bg-[#C69C4A] hover:bg-[#B38B3A] text-[#0C1713] font-semibold">
          <Plus className="w-4 h-4 mr-1.5" /> {saving ? "..." : "Créer l'employé"}
        </Button>
      </div>

      <Dialog open={!!tempPin} onOpenChange={(o) => { if (!o) setTempPin(null); }}>
        <DialogContent data-testid="staff-temp-pin-dialog" className="max-w-xs rounded-3xl bg-[#0C1713] border-[#1B3028] text-stone-100 text-center">
          {tempPin && (
            <>
              <DialogTitle className="text-lg flex items-center justify-center gap-2"><KeyRound className="w-5 h-5 text-[#C69C4A]" /> PIN provisoire · {tempPin.name}</DialogTitle>
              <DialogDescription className="text-stone-400 text-xs">Communiquez ce code à l'employé. Il est affiché <b>une seule fois</b> et expire dès sa première connexion : l'employé devra alors choisir son PIN personnel, que personne d'autre ne pourra voir.</DialogDescription>
              <p data-testid="staff-temp-pin-value" className="font-mono text-4xl font-extrabold tracking-[0.35em] text-[#C69C4A] py-3">{tempPin.pin}</p>
              <button data-testid="staff-temp-pin-close" onClick={() => setTempPin(null)} className="w-full h-11 rounded-full bg-[#C69C4A] text-[#0C1713] font-bold">J'ai noté le code</button>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!pinTarget} onOpenChange={(o) => { if (!o) { setPinTarget(null); } }}>
        <DialogContent data-testid="staff-pin-dialog" className="max-w-xs rounded-3xl bg-[#0C1713] border-[#1B3028] text-stone-100">
          {pinTarget && (
            <>
              <DialogTitle className="text-lg flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#C69C4A]" /> Réinitialiser le PIN de {pinTarget.name || pinTarget.email}</DialogTitle>
              <DialogDescription className="text-stone-400 text-xs">Le PIN actuel sera invalidé immédiatement. Un PIN provisoire sera généré et affiché une seule fois ; l'employé choisira ensuite son PIN personnel. Action journalisée.</DialogDescription>
              <div className="flex gap-2 pt-2">
                <button onClick={() => setPinTarget(null)} className="flex-1 h-11 rounded-full border border-[#1B3028] text-stone-300 text-sm">Annuler</button>
                <button data-testid="staff-pin-save" onClick={savePin} disabled={pinSaving} className="flex-1 h-11 rounded-full bg-[#C69C4A] hover:bg-[#B38B3A] text-[#0C1713] font-bold disabled:opacity-40">
                  {pinSaving ? "…" : "Réinitialiser"}
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={pinLenConfirm !== null} onOpenChange={(o) => { if (!o) setPinLenConfirm(null); }}>
        <DialogContent data-testid="pin-length-confirm-dialog" className="max-w-sm rounded-3xl bg-[#0C1713] border-[#1B3028] text-stone-100">
          <DialogTitle className="text-lg flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#C69C4A]" /> Passer à {pinLenConfirm} chiffres ?</DialogTitle>
          <DialogDescription className="text-stone-400 text-xs">
            Les PIN actuels ne seront PAS convertis. Chaque employé devra définir un nouveau PIN à {pinLenConfirm} chiffres (statut « Réinitialisation requise ») avant de se connecter par PIN.
          </DialogDescription>
          <div className="flex gap-2 pt-2">
            <button onClick={() => setPinLenConfirm(null)} className="flex-1 h-11 rounded-full border border-[#1B3028] text-stone-300 text-sm font-semibold">Annuler</button>
            <button data-testid="pin-length-confirm-apply" onClick={() => applyPinLength(pinLenConfirm)} disabled={pinLenSaving}
              className="flex-1 h-11 rounded-full bg-[#C69C4A] hover:bg-[#B38B3A] text-[#0C1713] text-sm font-bold disabled:opacity-40">
              {pinLenSaving ? "..." : "Confirmer"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
